import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowLeft,
  ChevronRight,
  Clock,
  BookOpen,
  ChevronLeft,
  ChevronDown,
  Menu,
  X
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "./LandingPage";
import { docsContent, getArticleBySlug, docCategories } from "../data/docsContent";

// Sidebar navigation component
const DocsSidebar = ({ currentSlug, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});
  
  // Group articles by category
  const articlesByCategory = {};
  Object.values(docsContent).forEach(article => {
    if (!articlesByCategory[article.category]) {
      articlesByCategory[article.category] = [];
    }
    articlesByCategory[article.category].push(article);
  });

  const toggleSection = (categoryId) => {
    setExpandedSections(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Auto-expand section containing current article
  useEffect(() => {
    const currentArticle = getArticleBySlug(currentSlug);
    if (currentArticle) {
      setExpandedSections(prev => ({
        ...prev,
        [currentArticle.category]: true
      }));
    }
  }, [currentSlug]);

  return (
    <nav className="space-y-1">
      {docCategories.map(category => {
        const articles = articlesByCategory[category.id] || [];
        const isExpanded = expandedSections[category.id];
        const hasCurrentArticle = articles.some(a => a.id === currentSlug);
        
        return (
          <div key={category.id}>
            <button
              onClick={() => toggleSection(category.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasCurrentArticle 
                  ? 'bg-primary/10 text-primary' 
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <span>{category.title}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {isExpanded && (
              <div className="ml-3 mt-1 space-y-1 border-l-2 border-muted pl-3">
                {articles.map(article => (
                  <Link
                    key={article.id}
                    to={`/docs/${article.id}`}
                    onClick={onClose}
                    className={`block px-3 py-1.5 rounded-md text-sm transition-colors ${
                      article.id === currentSlug
                        ? 'bg-primary text-white font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

// Simple markdown renderer (without external lib fallback)
const SimpleMarkdown = ({ content }) => {
  // Very basic markdown parsing
  const renderContent = (text) => {
    const lines = text.trim().split('\n');
    const elements = [];
    let currentList = [];
    let inCodeBlock = false;
    let codeBlockContent = [];
    
    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${index}`} className="bg-muted p-4 rounded-lg overflow-x-auto my-4 text-sm">
              <code>{codeBlockContent.join('\n')}</code>
            </pre>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        return;
      }
      
      if (inCodeBlock) {
        codeBlockContent.push(line);
        return;
      }
      
      // Headings
      if (line.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(<h2 key={index} className="text-2xl font-bold text-foreground mt-8 mb-4">{line.slice(3)}</h2>);
        return;
      }
      
      if (line.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(<h3 key={index} className="text-xl font-semibold text-foreground mt-6 mb-3">{line.slice(4)}</h3>);
        return;
      }
      
      if (line.startsWith('#### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(<h4 key={index} className="text-lg font-semibold text-foreground mt-4 mb-2">{line.slice(5)}</h4>);
        return;
      }
      
      // Horizontal rule
      if (line.startsWith('---')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(<hr key={index} className="my-8 border-border" />);
        return;
      }
      
      // Tables
      if (line.includes('|') && line.trim().startsWith('|')) {
        // Skip for now, tables need special handling
        return;
      }
      
      // List items
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.slice(2);
        currentList.push(
          <li key={`li-${index}`} className="text-muted-foreground">
            {renderInlineFormatting(content)}
          </li>
        );
        return;
      }
      
      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        const content = line.replace(/^\d+\.\s/, '');
        currentList.push(
          <li key={`li-${index}`} className="text-muted-foreground">
            {renderInlineFormatting(content)}
          </li>
        );
        return;
      }
      
      // Blockquotes
      if (line.startsWith('> ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <blockquote key={index} className="border-l-4 border-primary/30 pl-4 py-2 my-4 italic text-muted-foreground bg-muted/30 rounded-r-lg">
            {renderInlineFormatting(line.slice(2))}
          </blockquote>
        );
        return;
      }
      
      // Empty line
      if (line.trim() === '') {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        return;
      }
      
      // Regular paragraph
      if (currentList.length > 0) {
        elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
        currentList = [];
      }
      elements.push(
        <p key={index} className="text-muted-foreground my-4 leading-relaxed">
          {renderInlineFormatting(line)}
        </p>
      );
    });
    
    // Don't forget remaining list items
    if (currentList.length > 0) {
      elements.push(<ul key="final-ul" className="list-disc pl-6 my-4 space-y-2">{currentList}</ul>);
    }
    
    return elements;
  };
  
  const renderInlineFormatting = (text) => {
    // Bold
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Inline code
    text = text.replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    // Links
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>');
    
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };
  
  return <div className="prose-custom">{renderContent(content)}</div>;
};

export default function DocsArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const article = getArticleBySlug(slug);
  
  // Get prev/next articles
  const allArticles = Object.values(docsContent);
  const currentIndex = allArticles.findIndex(a => a.id === slug);
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;
  
  if (!article) {
    return (
      <div className="min-h-screen bg-[#f5f5f0]">
        <MarketingNav />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">The documentation article you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/docs">Back to Documentation</Link>
            </Button>
          </div>
        </div>
        <MarketingFooter />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />
      
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed bottom-6 right-6 z-50 p-3 bg-primary text-white rounded-full shadow-lg"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="pt-20 flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] w-72 bg-white border-r border-border
          overflow-y-auto z-40 transform transition-transform lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-4">
            <Link 
              to="/docs" 
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              All Documentation
            </Link>
            <DocsSidebar currentSlug={slug} onClose={() => setSidebarOpen(false)} />
          </div>
        </aside>
        
        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-6 py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
              <Link to="/docs" className="hover:text-foreground">Docs</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground">{article.title}</span>
            </nav>
            
            {/* Article header */}
            <header className="mb-8">
              <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {article.readTime} read
                </span>
                <span className="flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4" />
                  {docCategories.find(c => c.id === article.category)?.title}
                </span>
              </div>
            </header>
            
            {/* Article content */}
            <article className="prose-custom">
              <SimpleMarkdown content={article.content} />
            </article>
            
            {/* Prev/Next navigation */}
            <nav className="mt-12 pt-8 border-t border-border flex items-center justify-between gap-4">
              {prevArticle ? (
                <Link 
                  to={`/docs/${prevArticle.id}`}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground group"
                >
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Previous</div>
                    <div className="font-medium text-foreground">{prevArticle.title}</div>
                  </div>
                </Link>
              ) : <div />}
              
              {nextArticle ? (
                <Link 
                  to={`/docs/${nextArticle.id}`}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground group text-right"
                >
                  <div>
                    <div className="text-xs text-muted-foreground">Next</div>
                    <div className="font-medium text-foreground">{nextArticle.title}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : <div />}
            </nav>
          </div>
        </main>
      </div>
      
      <MarketingFooter />
    </div>
  );
}
